export interface BranchingScaleOption {
    branch: string;
    branchLabel: string;
    branchText: string;
    durationPrompt: string;
    label: string;
    value: number;
}

export interface BranchingScaleBranch {
    label: string;
    value: string;
    text: string;
    durationPrompt: string;
    options: Array<{
        label: string;
        value: number;
    }>;
}

interface RawBranchingScaleOption {
    branch?: unknown;
    branch_label?: unknown;
    branch_text?: unknown;
    duration_prompt?: unknown;
    label?: unknown;
    value?: unknown;
}

export function parseBranchingScaleOptions(scaleOptions: unknown): BranchingScaleBranch[] {
    if (!Array.isArray(scaleOptions)) return [];

    const branches = new Map<string, BranchingScaleBranch>();

    for (const rawOption of scaleOptions as RawBranchingScaleOption[]) {
        const branch = typeof rawOption.branch === "string" ? rawOption.branch.trim().toLowerCase() : "";
        const branchLabel = typeof rawOption.branch_label === "string" ? rawOption.branch_label.trim() : "";
        const branchText = typeof rawOption.branch_text === "string" ? rawOption.branch_text.trim() : "";
        const durationPrompt = typeof rawOption.duration_prompt === "string" ? rawOption.duration_prompt.trim() : "";
        const label = typeof rawOption.label === "string" ? rawOption.label.trim() : "";
        const value = Number(rawOption.value);

        if (!branch || !branchLabel || !branchText || !durationPrompt || !label || !Number.isFinite(value)) {
            continue;
        }

        const existingBranch = branches.get(branch);
        if (existingBranch) {
            existingBranch.options.push({ label, value });
            continue;
        }

        branches.set(branch, {
            label: branchLabel,
            value: branch,
            text: branchText,
            durationPrompt,
            options: [{ label, value }],
        });
    }

    return [...branches.values()];
}

export interface BranchingScaleSelection {
    branch: string;
    branchLabel: string;
    branchText: string;
    optionLabel: string;
    value: number;
}

/**
 * Compact text stored on entry_values.text_value.
 * Preferred: "A|>15 min|If you have no pain while undertaking sport"
 * Legacy: "A · >15 min"
 */
export function formatBranchingScaleSelectionText(selection: BranchingScaleSelection): string {
    return `${selection.branchLabel}|${selection.optionLabel}|${selection.branchText}`;
}

export function parseBranchingScaleSelectionText(text: string | null | undefined): {
    branchLabel: string;
    optionLabel: string;
    branchText?: string;
} | null {
    if (!text) return null;

    const pipeParts = text
        .split("|")
        .map((part) => part.trim())
        .filter(Boolean);
    if (pipeParts.length >= 2) {
        return {
            branchLabel: pipeParts[0]!,
            optionLabel: pipeParts[1]!,
            branchText: pipeParts[2] || undefined,
        };
    }

    const match = text.trim().match(/^(.+?)\s*[·|]\s*(.+)$/);
    const branchLabel = match?.[1]?.trim();
    const optionLabel = match?.[2]?.trim();
    if (!branchLabel || !optionLabel) return null;
    return { branchLabel, optionLabel };
}

/**
 * Resolve the selected parent branch + child option for a saved score.
 * Prefer an explicit branch hint (from form or stored text) when the score is shared across branches.
 */
export function findBranchingScaleSelection(
    scaleOptions: unknown,
    value: number | null | undefined,
    branchHint?: string | null
): BranchingScaleSelection | null {
    if (value == null || !Number.isFinite(Number(value))) return null;

    const score = Number(value);
    const branches = parseBranchingScaleOptions(scaleOptions);
    const matches: BranchingScaleSelection[] = [];

    for (const branch of branches) {
        const option = branch.options.find((candidate) => candidate.value === score);
        if (!option) continue;
        matches.push({
            branch: branch.value,
            branchLabel: branch.label,
            branchText: branch.text,
            optionLabel: option.label,
            value: score,
        });
    }

    if (matches.length === 0) return null;

    const normalisedHint = typeof branchHint === "string" ? branchHint.trim().toLowerCase() : "";
    if (normalisedHint) {
        const byValue = matches.find((match) => match.branch === normalisedHint);
        if (byValue) return byValue;

        const byLabel = matches.find((match) => match.branchLabel.toLowerCase() === normalisedHint);
        if (byLabel) return byLabel;
    }

    return matches[0] ?? null;
}
